import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Calendar, Paintbrush, Star, Gamepad2, Settings, Award, ArrowRight, X } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_mood-diary-calendar/artifacts/sd96v0rx_image.png';

// Welcome Willow star SVG (same as sticker)
const WelcomeStar = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full">
    <polygon points="40,8 48,30 72,30 52,44 60,68 40,54 20,68 28,44 8,30 32,30" fill="#FFD700" stroke="#FFA500" strokeWidth="1" />
    <circle cx="40" cy="38" r="8" fill="white" opacity="0.3" />
    <text x="40" y="42" textAnchor="middle" fontSize="10" fill="#8B6914" fontWeight="bold">W</text>
  </svg>
);

const STEPS = [
  {
    id: 'welcome',
    target: null,
    title: "Welcome to Willow Notes!",
    body: "Your cozy little corner to write, draw, and collect beautiful things. Let me show you around!",
    icon: Sparkles,
    position: 'center',
  },
  {
    id: 'diary',
    target: '[data-testid="diary-card"]',
    title: "Your Diary",
    body: "This is where the magic happens! Pick a theme, choose your mood, and write about your day. Each day you write, your streak grows and a new bird hatches!",
    icon: Paintbrush,
    position: 'left',
  },
  {
    id: 'calendar',
    target: '[data-testid="calendar-card"]',
    title: "Your Calendar",
    body: "Keep track of your diary entries here! Days with entries are marked with a soft dot. Click any date to peek at what you wrote.",
    icon: Calendar,
    position: 'right',
  },
  {
    id: 'mark-day',
    target: '[data-testid="add-important-date-button"]',
    title: "Mark Special Days",
    body: "Got a birthday, event, or special memory coming up? Tap this to add a coloured star to any date so you never forget!",
    icon: Star,
    position: 'right',
  },
  {
    id: 'game',
    target: '[data-testid="wyr-toggle"]',
    title: "Would You Rather?",
    body: "Need a break? This little game gives you 30 cozy 'Would You Rather' questions every day. Tap to slide it open!",
    icon: Gamepad2,
    position: 'left',
  },
  {
    id: 'settings',
    target: '[data-testid="settings-gear"]',
    title: "Settings",
    body: "Switch between colour palettes and toggle dark mode here. There are 3 beautiful palettes to match your vibe!",
    icon: Settings,
    position: 'right',
  },
  {
    id: 'nav',
    target: '[data-testid="nav-drawings"]',
    title: "Drawings & Awards",
    body: "Explore the Drawings tab to create art with brushes, pens, and a fill tool. Check Awards to see all the cute stickers you can collect!",
    icon: Award,
    position: 'bottom',
  },
  {
    id: 'award',
    target: null,
    title: "Your First Award!",
    body: "You just earned the Welcome Willow star for joining! Head to the Awards page to see your collection grow.",
    icon: null,
    position: 'center',
    isAward: true,
  },
];

export default function OnboardingTour({ onComplete }) {
  const [step, setStep] = useState(0);
  const [highlight, setHighlight] = useState(null);
  const [entering, setEntering] = useState(true);
  const tooltipRef = useRef(null);

  const current = STEPS[step];

  useEffect(() => {
    setEntering(true);
    const timer = setTimeout(() => setEntering(false), 300);

    if (current.target) {
      const el = document.querySelector(current.target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          const rect = el.getBoundingClientRect();
          setHighlight({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
        }, 400);
      } else {
        setHighlight(null);
      }
    } else {
      setHighlight(null);
    }

    return () => clearTimeout(timer);
  }, [step, current.target]);

  const next = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };

  const skip = () => onComplete();

  // Tooltip position relative to highlight
  const getTooltipStyle = () => {
    if (!highlight || current.position === 'center') {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    const pad = 16;
    const pos = current.position;

    if (pos === 'right') {
      return {
        top: Math.max(20, highlight.top + highlight.height / 2 - 100),
        left: Math.min(window.innerWidth - 380, highlight.left + highlight.width + pad),
      };
    }
    if (pos === 'left') {
      return {
        top: Math.max(20, highlight.top + highlight.height / 2 - 100),
        left: Math.max(16, highlight.left - 360 - pad),
      };
    }
    if (pos === 'bottom') {
      return {
        top: highlight.top + highlight.height + pad,
        left: Math.max(16, highlight.left + highlight.width / 2 - 170),
      };
    }
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  };

  const isCenter = !highlight || current.position === 'center';
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-[200]" data-testid="onboarding-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />

      {/* Spotlight cutout */}
      {highlight && (
        <div
          className="absolute rounded-2xl ring-4 ring-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] transition-all duration-500 ease-out z-[201]"
          style={{
            top: highlight.top - 8,
            left: highlight.left - 8,
            width: highlight.width + 16,
            height: highlight.height + 16,
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className={`absolute z-[202] w-[340px] bg-white rounded-3xl shadow-2xl border-[1.5px] border-[var(--border-soft)] p-6 transition-all duration-300 ${entering ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
        style={getTooltipStyle()}
        data-testid={`onboarding-step-${current.id}`}
      >
        {/* Welcome step — centered with logo */}
        {current.id === 'welcome' && (
          <div className="text-center">
            <img src={LOGO_URL} alt="Willow Notes" className="h-20 object-contain mx-auto mb-2" />
            <h2 className="text-3xl font-heading font-bold text-[#1A1A1A] mb-3">{current.title}</h2>
            <p className="text-[#4A5568] text-sm leading-relaxed mb-6">{current.body}</p>
            <button
              onClick={next}
              className="w-full py-3 rounded-full bg-[var(--c2)] hover:bg-[var(--c2-hover)] text-[#1A1A1A] font-heading font-bold text-lg shadow-sm transition-all"
              data-testid="onboarding-start"
            >
              Show me around <ArrowRight className="w-4 h-4 inline ml-1" />
            </button>
            <button onClick={skip} className="mt-3 text-xs text-[#9CA3AF] hover:text-[#4A5568] transition-colors" data-testid="onboarding-skip">
              I'll figure it out myself
            </button>
          </div>
        )}

        {/* Award step — centered with star */}
        {current.isAward && (
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-3 animate-spin-slow">
              <WelcomeStar />
            </div>
            <h2 className="text-2xl font-heading font-bold text-[#1A1A1A] mb-2">{current.title}</h2>
            <p className="text-[#4A5568] text-sm leading-relaxed mb-5">{current.body}</p>
            <button
              onClick={next}
              className="w-full py-3 rounded-full bg-[var(--c1)] hover:bg-[var(--c1-hover)] text-[#1A1A1A] font-heading font-bold text-lg shadow-sm transition-all"
              data-testid="onboarding-finish"
            >
              Let's go!
            </button>
          </div>
        )}

        {/* Regular spotlight steps */}
        {current.id !== 'welcome' && !current.isAward && (
          <>
            <div className="flex items-center gap-3 mb-3">
              {Icon && (
                <div className="w-10 h-10 rounded-full bg-[var(--c1)] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#1A1A1A]" />
                </div>
              )}
              <h3 className="text-xl font-heading font-bold text-[#1A1A1A]">{current.title}</h3>
            </div>
            <p className="text-[#4A5568] text-sm leading-relaxed mb-5">{current.body}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#9CA3AF] font-medium">{step} / {STEPS.length - 1}</span>
              <div className="flex gap-2">
                <button onClick={skip} className="px-4 py-2 rounded-full text-sm text-[#6B7280] hover:bg-[#F3F4F6] transition-colors" data-testid="onboarding-skip-step">
                  Skip
                </button>
                <button
                  onClick={next}
                  className="px-5 py-2 rounded-full bg-[var(--c2)] hover:bg-[var(--c2-hover)] text-[#1A1A1A] font-bold text-sm shadow-sm transition-all"
                  data-testid="onboarding-next"
                >
                  {step === STEPS.length - 2 ? 'Last one!' : 'Next'} <ArrowRight className="w-3.5 h-3.5 inline ml-0.5" />
                </button>
              </div>
            </div>
            {/* Progress dots */}
            <div className="flex gap-1.5 justify-center mt-4">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? 'w-6 bg-[var(--c1)]' : i < step ? 'w-1.5 bg-[var(--c2)]' : 'w-1.5 bg-[#E5E7EB]'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
